import React, { useState } from "react";
import { Button, Badge, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { Icon } from "../../components/Component";
import { handleGetApis, handlePostApis } from "../../service/Api";

// Must match ryzhm_api/services/shipping.service.js (STAGES, CANCELLABLE_LABELS).
const SHIPPABLE = ["Created", "Confirmed", "Processing"];
const CANCELLABLE = ["Manifested"];
const STAGE_COLOR = {
  Manifested: "info",
  "In Transit": "primary",
  "Out for Delivery": "primary",
  Delivered: "success",
  "Returning to Origin": "warning",
  "Returned to Origin": "danger",
  Cancelled: "secondary",
  Lost: "danger",
};

const trackUrl = (awb) => `https://www.delhivery.com/track-v2/package/${encodeURIComponent(awb)}`;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) : null;

/**
 * Delhivery block in the order detail modal: create the AWB, print the label,
 * refresh tracking, cancel before pickup. `onChanged` reloads the order list.
 */
const ShippingPanel = ({ order, config, onChanged }) => {
  const [busy, setBusy] = useState(null); // action name while a request runs

  const run = async (action, request, { confirmText } = {}) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(action);
    try {
      const res = await request();
      if (res?.data?.success) {
        toast.success(res.data.message || "Done");
        await onChanged();
      } else {
        toast.error(res?.data?.message || "Request failed");
      }
    } finally {
      setBusy(null);
    }
  };

  const openLabel = async () => {
    // Opened before the request so the browser doesn't treat it as a popup.
    const tab = window.open("", "_blank");
    setBusy("label");
    try {
      const res = await handleGetApis(`admin/shipping/${order.id}/label`);
      if (res?.data?.success && res.data.data?.url) {
        if (tab) tab.location.href = res.data.data.url;
        else window.location.href = res.data.data.url;
      } else {
        tab?.close();
        toast.error(res?.data?.message || "Label not available yet. Try again in a minute.");
      }
    } finally {
      setBusy(null);
    }
  };

  const isDelhivery = order.courier_name === "Delhivery" && order.tracking_id;
  const canShip = config?.enabled && !order.tracking_id && order.payment_status === "paid" && SHIPPABLE.includes(order.order_status);

  return (
    <div className="bg-light p-3 rounded mb-3 mb-sm-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <span className="text-soft fs-11px fw-bold text-uppercase">
          <Icon name="truck" className="me-1" /> Shipping
        </span>
        {config?.enabled && config.environment !== "production" && (
          <Badge color="warning" className="badge-dim">Delhivery staging</Badge>
        )}
      </div>

      {order.tracking_id ? (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <span className="text-dark fs-13px fw-bold">{order.courier_name || "Courier"}</span>
            <span className="fs-13px" style={{ fontFamily: "monospace" }}>AWB {order.tracking_id}</span>
            {order.shipment_status && (
              <Badge color={STAGE_COLOR[order.shipment_status] || "secondary"} className="badge-dim">
                {order.shipment_status}
              </Badge>
            )}
          </div>
          <div className="text-soft fs-12px mb-3">
            {order.shipment_status_at && <>Last update {fmtDate(order.shipment_status_at)}</>}
            {order.shipment_expected_at && (
              <> · Expected {new Date(order.shipment_expected_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</>
            )}
          </div>
          {isDelhivery && (
            <div className="d-flex flex-wrap gap-2">
              <Button size="sm" color="primary" onClick={openLabel} disabled={!!busy}>
                {busy === "label" ? <Spinner size="sm" /> : <Icon name="printer" />}
                <span>Shipping Label</span>
              </Button>
              <Button
                size="sm" color="light" outline disabled={!!busy}
                onClick={() => run("sync", () => handlePostApis(`admin/shipping/${order.id}/sync`))}
              >
                {busy === "sync" ? <Spinner size="sm" /> : <Icon name="reload" />}
                <span>Refresh Tracking</span>
              </Button>
              <a className="btn btn-sm btn-light" href={trackUrl(order.tracking_id)} target="_blank" rel="noopener noreferrer">
                <Icon name="external" /> <span>Delhivery</span>
              </a>
              {CANCELLABLE.includes(order.shipment_status) && (
                <Button
                  size="sm" color="danger" outline disabled={!!busy}
                  onClick={() =>
                    run("cancel", () => handlePostApis(`admin/shipping/${order.id}/cancel`), {
                      confirmText: `Cancel Delhivery shipment ${order.tracking_id}? The order goes back to Confirmed and can be shipped again.`,
                    })
                  }
                >
                  {busy === "cancel" ? <Spinner size="sm" /> : <Icon name="cross-circle" />}
                  <span>Cancel Shipment</span>
                </Button>
              )}
            </div>
          )}
        </>
      ) : canShip ? (
        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
          <span className="text-soft fs-12px">
            Pack the parcel first. This creates the AWB at Delhivery for <strong>{config.pickup_location}</strong>.
          </span>
          <Button
            size="sm" color="primary" disabled={!!busy}
            onClick={() =>
              run("ship", () => handlePostApis(`admin/shipping/${order.id}/ship`), {
                confirmText: `Create a Delhivery shipment for #${order.order_ref}?`,
              })
            }
          >
            {busy === "ship" ? <Spinner size="sm" /> : <Icon name="truck" />}
            <span>Ship with Delhivery</span>
          </Button>
        </div>
      ) : (
        <span className="text-soft fs-12px">
          {!config?.enabled
            ? "Delhivery is not connected yet (add DELHIVERY_TOKEN to the API .env). Mark status manually for now."
            : order.payment_status !== "paid"
              ? "Not paid. This order can't be shipped."
              : `No shipment. A ${order.order_status} order can't be shipped.`}
        </span>
      )}
    </div>
  );
};

export default ShippingPanel;
